import { BASE_URL, IMAGE_CLOUD_FOLDER, IMAGE_CLOUD_NAME, IMAGE_CLOUD_PRESET } from '../settings';

// Agent image-enhance lives inside the main backend (searchservice).
// Default: `${BASE_URL}search/agent` → /api/search/agent via the API gateway.
const AGENT_API_URL = process.env.REACT_APP_AGENT_URL || `${BASE_URL}search/agent`;

/**
 * Fetches a remote image URL and returns a File for the enhance API.
 */
export async function fetchImageAsFile(imageUrl, fileName = 'space-image.jpg') {
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error('Could not load the image. Please try again.');
  }
  const blob = await response.blob();
  const extension = blob.type === 'image/png' ? 'png' : 'jpg';
  const baseName = fileName.replace(/\.[^.]+$/, '') || 'space-image';
  return new File([blob], `${baseName}.${extension}`, { type: blob.type || 'image/jpeg' });
}

/**
 * Sends an image file to the agent enhance endpoint; returns the enhanced PNG blob.
 */
export async function enhanceImageFile(file) {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(`${AGENT_API_URL}/image/enhance`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Enhancement failed (${response.status})`);
  }

  return response.blob();
}

/**
 * Uploads a blob to Cloudinary (unsigned preset) and normalizes to widget-style info.
 */
export async function uploadBlobToCloudinary(blob, fileName = 'enhanced.png') {
  const formData = new FormData();
  formData.append('file', blob, fileName);
  formData.append('upload_preset', IMAGE_CLOUD_PRESET);
  formData.append('folder', IMAGE_CLOUD_FOLDER);

  const uploadUrl = `https://api.cloudinary.com/v1_1/${IMAGE_CLOUD_NAME}/image/upload`;
  const response = await fetch(uploadUrl, { method: 'POST', body: formData });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    const message = (errData.error && errData.error.message) || errData.error;
    throw new Error(message || 'Failed to save enhanced image. Please try again.');
  }

  const data = await response.json();
  return {
    ...data,
    url: data.secure_url || data.url,
    resource_type: data.resource_type || 'image',
  };
}
